import { Box, Button, IconButton, TextField, Typography } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { Controller, Form } from 'react-hook-form';
import { isNil } from 'lodash';
import { useManageTeam } from '../hooks';
import { t } from 'i18next';
import { USStateSelect } from 'components/USStateSelect';
import { Select } from 'ui';
import { SaveChangesModal } from '@components';

interface IManageTeamProps {
  teamId: string;
}

export const ManageTeam = ({ teamId }: IManageTeamProps) => {
  const {
    control,
    watch,
    onSubmit,
    isSubmitting,
    teamsShort,
    partnerShort,
    organizationsShort,
    practicesShort,
    recordTypes,
    fields,
    append,
    isSubmitEnabled,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    isValid,
    isEditSubmitting,
    remove,
  } = useManageTeam({ id: teamId });

  return (
    <Form
      control={control}
      onSubmit={e => onSubmit(e.event)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        borderRadius: 0,
        gap: '1rem',
        paddingTop: 3,
      }}
    >
      <Controller
        name='name'
        control={control}
        render={({ field, fieldState: { error } }) => {
          return (
            <Select
              {...field}
              size='medium'
              label={t('adminPanel.teams.drawer.generalTab.name')}
              error={!isNil(error)}
              required
              options={teamsShort}
              helperText={error?.message}
            />
          );
        }}
      />
      <Controller
        name='description'
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            type='text'
            size='medium'
            label={t('adminPanel.teams.drawer.generalTab.description')}
            variant='outlined'
            error={!isNil(error)}
            helperText={error ? error.message : null}
            fullWidth
          />
        )}
      />
      <Box
        sx={{
          width: '100%',
        }}
      >
        <Typography variant='subtitle2' sx={{ mb: 1, mt: 1 }}>
          {t('adminPanel.teams.drawer.generalTab.managing_record')}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            gap: 1,
            mb: 1,
          }}
        >
          <Controller
            name='record_type'
            control={control}
            render={({ field, fieldState: { error } }) => {
              return (
                <Select
                  {...field}
                  size='medium'
                  label={t('adminPanel.teams.drawer.generalTab.record_type')}
                  error={!isNil(error)}
                  required
                  options={recordTypes}
                  helperText={error?.message}
                />
              );
            }}
          />
          <Controller
            name='record_id'
            control={control}
            render={({ field, fieldState: { error } }) => {
              return (
                <Select
                  {...field}
                  size='medium'
                  label={t('adminPanel.teams.drawer.generalTab.record_name')}
                  error={!isNil(error)}
                  required
                  options={
                    (
                      {
                        partner: partnerShort,
                        organization: organizationsShort,
                        practice: practicesShort,
                      } as const
                    )[watch(`record_type`)] ?? organizationsShort
                  }
                  helperText={error?.message}
                />
              );
            }}
          />
        </Box>
      </Box>
      <Box
        sx={{
          width: '100%',
        }}
      >
        <Typography variant='subtitle2' sx={{ mb: 1, mt: 1 }}>
          {t('adminPanel.teams.drawer.generalTab.other_records')}
        </Typography>
        {fields?.map((field, index) => (
          <Box
            key={field.id}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              gap: 1,
              mb: 1,
            }}
          >
            <Controller
              name={`records.${index}.record_type`}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Select
                  {...field}
                  size='medium'
                  label={t('adminPanel.teams.drawer.generalTab.record_type')}
                  error={!isNil(error)}
                  options={recordTypes}
                  required
                  helperText={error?.message}
                />
              )}
            />

            <Controller
              name={`records.${index}.record_id`}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Select
                  {...field}
                  size='medium'
                  label={t('adminPanel.teams.drawer.generalTab.record_name')}
                  error={!isNil(error)}
                  required
                  options={
                    (
                      {
                        partner: partnerShort,
                        organization: organizationsShort,
                        practice: practicesShort,
                      } as const
                    )[watch(`records.${index}.record_type`)!] ??
                    organizationsShort
                  }
                  helperText={error?.message}
                />
              )}
            />
            <IconButton
              onClick={() => remove(index)}
              sx={{ height: 40, width: 40 }}
            >
              <Delete color='error' />
            </IconButton>
          </Box>
        ))}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant='text'
            startIcon={<Add />}
            onClick={() => {
              append({ record_type: 'organization', record_id: '' });
            }}
          >
            {t('general.add')}
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          width: '100%',
        }}
      >
        <Typography variant='subtitle2' sx={{ mb: 1, mt: 1 }}>
          {t('adminPanel.teams.drawer.generalTab.contactInformation')}
        </Typography>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'row',
            gap: '0.5rem',
          }}
        >
          <Controller
            name='city'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                type='text'
                size='medium'
                label={t('adminPanel.teams.drawer.generalTab.city')}
                variant='outlined'
                error={!isNil(error)}
                helperText={error ? error.message : null}
                fullWidth
              />
            )}
          />
          <Controller
            name='state'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <USStateSelect
                {...field}
                value={field.value ?? undefined}
                size='medium'
                error={!!error}
                helperText={error ? error.message : undefined}
              />
            )}
          />
        </Box>
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-end',
          pt: '0.5rem',
          position: 'sticky',
          bottom: '0',
          left: 0,
          bgcolor: theme => theme.palette.common.white,
          zIndex: 10,
        }}
      >
        <Button
          type='submit'
          variant='contained'
          color='secondary'
          sx={{
            fontSize: '0,875rem',
            fontWeight: 'medium',
            lineHeight: '24px',
            color: 'white',
          }}
          disabled={!isSubmitEnabled}
          loading={isSubmitting}
        >
          {t('general.saveChanges')}
        </Button>
      </Box>
      <SaveChangesModal
        isOpen={isSaveChangesModalOpen}
        onClose={handleCloseSaveChangesModal}
        isValid={isValid}
        handleSaveChanges={handleSaveChanges}
        discardChanges={handleDiscardChanges}
        isLoading={isEditSubmitting}
      />
    </Form>
  );
};
